import { ComponentConfig } from '@measured/puck';
import {
  IDropdownOptions,
  navigationPathField,
} from '../../../../page-builder/puck/reusable-props/navigation-path-field';
import { AxiosInstance } from 'axios';
import CustomTooltip from '../../../../components/common/tooltip';
import { ColorProps, colorField } from '../../../puck/reusable-props/color';
import { MarginProps, marginFields } from '../../../puck/reusable-props/margin';
import { HeightProps, heightField } from '../../../puck/reusable-props/height';
import { WidthProps, widthField } from '../../../puck/reusable-props/width';
import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
  getPaddingStyles,
  getQuillEditorText,
  resolveShowComponentField,
} from '../../../utils/helper';
import showComponent, {
  showComponentProps,
} from '../../../puck/reusable-props/showComponent';
import {
  JustifyContentField,
  JustifyContentProps,
} from '../../../puck/reusable-props/justify-content';
import {
  PaddingProps,
  paddingFields,
} from '../../../puck/reusable-props/padding';
import { ReduxSelectorProps } from '../../../services/constants';
import Border, {
  IBorderValueProps,
  borderProps,
} from '../../../puck/reusable-props/Border';
import { QuillTextEditor } from '../../../puck/reusable-props/quill-editor';
import { boxShadows } from '../../../../page-builder/puck/reusable-props/options';
import '../../text-editor/style.scss';

export type ButtonSectionProps = {
  Button: string;
  border: IBorderValueProps;
  nextPagePath: IDropdownOptions;
  boxShadow: 'none' | 'shadow-md' | 'shadow-lg' | 'shadow-xl' | 'shadow-2xl';
} & ColorProps &
  MarginProps &
  PaddingProps &
  showComponentProps &
  HeightProps &
  WidthProps &
  JustifyContentProps;

export interface ButtonComponentProps extends ReduxSelectorProps {
  baseService?: AxiosInstance;
}

export const ButtonComponent = ({
  useAppSelector,
}: ButtonComponentProps): ComponentConfig<ButtonSectionProps> => {
  return {
    label: (
      <CustomTooltip title="Button">
        <span>Button</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      ...navigationPathField(),
      Button: { ...QuillTextEditor('Button Text', false).content },
      ...colorField,
      ...marginFields,
      ...paddingFields,
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
      boxShadow: {
        label: 'Shadow',
        type: 'select',
        options: boxShadows,
      },
      ...heightField,
      ...widthField,
      ...JustifyContentField,
    },
    resolveFields: ({ props }, { fields }) =>
      resolveShowComponentField(props.id, fields),
    defaultProps: {
      showComponent: ['desktop', 'tablet', 'mobile'],
      nextPagePath: { label: '', value: '' },
      Button: `<p style="line-height:1.5;"><strong style="font-size: 24px;">Button</strong></p>`,
      boxShadow: 'none',
      margin: {
        desktop: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
      },
      justifyContent: 'justify-center',
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingLeft: {
          desktop: '4',
          mobile: '2',
          tablet: '4',
        },
        paddingRight: {
          desktop: '4',
          mobile: '4',
          tablet: '2',
        },
        paddingTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
      ...borderProps,
      color: {
        backgroundColor: '#FFFFFF',
        hoverColor: '#FFFFFF',
      },
      height: 'h-12',
      width: 'w-auto',
    },
    render: ({
      nextPagePath,
      Button,
      margin,
      justifyContent,
      padding,
      border,
      boxShadow,
      color,
      height,
      width,
      showComponent,
      editMode,
    }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const handleClick = (): void => {
        if (!nextPagePath?.value) return;
        const formattedUrl = nextPagePath?.value?.startsWith('http')
          ? nextPagePath?.value
          : `https://${nextPagePath?.value}`;
        window.open(formattedUrl, '_self');
      };
      const marginStyles = getMarginStyles(margin, currentDeviceType);
      const state = useAppSelector(state => state);
      const newButton = getQuillEditorText(Button, state);
      return (
        <>
          {showComponent?.includes(currentDeviceType) && (
            <div
              className={`flex ${justifyContent} text-editor-content`}
              style={{
                ...marginStyles,
              }}>
              <button
                className={`bg-page_builder_theme ${height} ${width} ${boxShadow} ${border?.borderStyle?.[currentDeviceType]}`}
                style={{
                  backgroundColor: color?.backgroundColor,
                  ...getBorderProps(border, currentDeviceType),
                  ...getPaddingStyles(padding, currentDeviceType),
                  lineHeight: 1.5,
                  transition: 'background-color 0.3s',
                }}
                onMouseOver={e => {
                  e.currentTarget.style.backgroundColor = color?.hoverColor;
                }}
                onMouseOut={e => {
                  e.currentTarget.style.backgroundColor =
                    color?.backgroundColor;
                }}
                onClick={handleClick}
                dangerouslySetInnerHTML={{ __html: newButton }}></button>
            </div>
          )}
        </>
      );
    },
  };
};
